import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import useInterfaceSounds from './utils/filemanager/useInterfaceSounds';
import './App.css';
import SplashScreen from './components/splashscreen/SplashScreen';
import RealityStackViewer from './components/harvester/three/RealityStackViewer';
import P5Sketch from './components/harvester/scanner/P5Sketch';
import LinkAccount from './components/account/LinkAccount';
import DiscordCallback from './components/account/DiscordCallback';
import MinecraftCallback from './components/account/MinecraftCallback';
import quantumEngine from './utils/engine/QuantumEngine';
import { LoreProvider } from './components/harvester/scanner/LoreProvider';
import { NotificationProvider } from './context/NotificationContext';
import './utils/TestCrossDomain'; // Load cross-domain testing utilities
import Dashboard from './components/dashboard/Dashboard';
import ReferralNetworkModule from './components/referralnetwork/ReferralNetworkModule';
import FragmentRedemption from './components/vault/FragmentRedemption';
import PlayerService from './services/PlayerService';

function MainApp({ linkedProfile, onProfileUpdate }) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasStartedScanning, setHasStartedScanning] = useState(false);
  const [showP5Sketch, setShowP5Sketch] = useState(false);
  const [showDashboard, setShowDashboard] = useState(true);
  const [showReferralNetwork, setShowReferralNetwork] = useState(false);
  const [showFragmentRedemption, setShowFragmentRedemption] = useState(false);
  const [showAccessDeniedModal, setShowAccessDeniedModal] = useState(false);
  const [selectedLayer, setSelectedLayer] = useState(null);

  // Initialize global interface sounds
  useInterfaceSounds();

  // Check for referral code in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const referralCode = urlParams.get('ref');
    if (referralCode) {
      // Normalize to uppercase and trim whitespace
      const normalizedCode = referralCode.toUpperCase().trim();
      console.log('Found referral code in URL:', normalizedCode);
      localStorage.setItem('pending_referral', normalizedCode);
      // Also store timestamp to track when code was captured
      localStorage.setItem('pending_referral_timestamp', Date.now().toString());
      // Remove ref from URL without refreshing
      const newUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  const handleLoadingComplete = () => {
    setIsLoading(false);
  };

  const handleStartScanning = () => {
    setHasStartedScanning(true);
  };

  const [selectedSeed, setSelectedSeed] = useState(null);

  const handlePlaneClick = (layer, seed) => {
    setSelectedLayer(layer);
    setSelectedSeed(seed);
    setShowP5Sketch(true);
  };

  const handleP5SketchClose = () => {
    setShowP5Sketch(false);
  };

  const handleHarvesterClick = () => {
    setShowDashboard(false);
    setShowReferralNetwork(false);
    setShowFragmentRedemption(false);
  };

  const handleReferralNetworkClick = () => {
    setShowDashboard(false);
    setShowReferralNetwork(true);
    setShowFragmentRedemption(false);
  };

  const handleFragmentRedemptionClick = async () => {
    // Check if user has visited IDCU-OS before allowing access to Defragger
    if (!linkedProfile?.playerId) {
      setShowAccessDeniedModal(true);
      return;
    }

    const hasVisitedIdcuOs = await PlayerService.checkIdcuOsVisitor(linkedProfile.playerId);
    if (!hasVisitedIdcuOs) {
      setShowAccessDeniedModal(true);
      return;
    }

    setShowDashboard(false);
    setShowReferralNetwork(false);
    setShowFragmentRedemption(true);
  };

  const handleCloseAccessDeniedModal = () => {
    setShowAccessDeniedModal(false);
  };

  const handleBackToDashboard = () => {
    setShowDashboard(true);
    setShowReferralNetwork(false);
    setShowFragmentRedemption(false);
  };

  return (
    <div className="App">
      <LinkAccount onAccountLinked={onProfileUpdate} />
      {isLoading ? (
        <SplashScreen onLoadingComplete={handleLoadingComplete} />
      ) : !hasStartedScanning ? (
        <SplashScreen showStartButton onStartScanning={handleStartScanning} />
      ) : showP5Sketch ? (
        <P5Sketch
          onClose={handleP5SketchClose}
          linkedProfile={linkedProfile}
          onUpdateProfile={onProfileUpdate}
          selectedLayer={selectedLayer}
          selectedSeed={selectedSeed}
        />
      ) : showDashboard ? (
        <Dashboard
          onHarvesterClick={handleHarvesterClick}
          onReferralNetworkClick={handleReferralNetworkClick}
          onFragmentRedemptionClick={handleFragmentRedemptionClick}
        />
      ) : showReferralNetwork ? (
        <ReferralNetworkModule
          onBack={handleBackToDashboard}
          linkedProfile={linkedProfile}
        />
      ) : showFragmentRedemption ? (
        <FragmentRedemption
          onBack={handleBackToDashboard}
          linkedProfile={linkedProfile}
        />
      ) : (
        <RealityStackViewer
          onPlaneClick={handlePlaneClick}
          linkedProfile={linkedProfile}
          onProfileUpdate={onProfileUpdate}
          onBack={handleBackToDashboard}
        />
      )}

      {/* Access Denied Modal for Defragger */}
      {showAccessDeniedModal && (
        <div className="access-denied-modal-overlay">
          <div className="access-denied-modal-backdrop" onClick={handleCloseAccessDeniedModal} />
          <div className="access-denied-modal-container">
            <div className="access-denied-modal-header">
              <h2>[ACCESS DENIED]</h2>
              <button onClick={handleCloseAccessDeniedModal} className="access-denied-modal-close">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
              </button>
            </div>
            <div className="access-denied-modal-content">
              <div className="access-denied-modal-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M4.93 4.93l14.14 14.14"/>
                </svg>
              </div>
              <div className="access-denied-modal-title">DEFRAGGER ACCESS RESTRICTED</div>
              <div className="access-denied-modal-message">
                DESTINATION REQUIREMENTS NOT MET
              </div>
            </div>
            <button onClick={handleCloseAccessDeniedModal} className="access-denied-modal-button">
              ACKNOWLEDGED
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function App() {
  const [linkedProfile, setLinkedProfile] = useState(null);
  const [showCopiedToast, setShowCopiedToast] = useState(false);

  const handleProfileUpdate = (profile) => {
    setLinkedProfile(profile);
    if (profile && profile.data) {
      const qubits = parseInt(profile.data.qubits) || 0;
      quantumEngine.setHarvestedQubits(qubits);
    }
  };

  const handleCopyEmail = () => {
    navigator.clipboard.writeText('info.mindfabric@gmail.com');
    setShowCopiedToast(true);
    setTimeout(() => setShowCopiedToast(false), 2000);
  };

  return (
    <BrowserRouter basename={process.env.PUBLIC_URL || "/"}>
      <NotificationProvider>
        <Routes>
          <Route
            path="/discord/callback"
            element={<DiscordCallback />}
          />
          <Route
            path="/minecraft/callback"
            element={<MinecraftCallback />}
          />
          <Route
            path="/"
            element={
              <LoreProvider>
                <MainApp
                  linkedProfile={linkedProfile}
                  onProfileUpdate={handleProfileUpdate}
                />
              </LoreProvider>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <div className="mind-fabric-credit">
          UI by <span className="mind-fabric-link" onClick={handleCopyEmail}>Mind Fabric</span>
          <div className={`mind-fabric-toast ${showCopiedToast ? 'visible' : ''}`}>Email copied to clipboard</div>
        </div>
      </NotificationProvider>
    </BrowserRouter>
  );
}

export default App;